import React, { useState } from "react";
import { toast } from "sonner";
import { useLang } from "../i18n/LanguageContext";
import { api } from "../lib/api";
import { useSeo, Icon, Reveal } from "../components/common";
import { PageHero } from "../components/PageHero";
import { CONTACT, whatsappLink, IMG } from "../data/site";

export default function Contact() {
  const { t } = useLang();
  useSeo("Contact Sparko | Cleaning Services Netherlands", "Contact Sparko for premium cleaning services in the Netherlands. Call, WhatsApp or send a message. KVK 93357559.");
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/submissions", { type: "contact", ...form });
      toast.success(t.form.success);
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch { toast.error(t.form.error); }
    setSending(false);
  };

  const cards = [
    { icon: "Phone", label: t.cta.call, value: CONTACT.phone, href: `tel:${CONTACT.phoneRaw}` },
    { icon: "MessageCircle", label: "WhatsApp", value: CONTACT.phone, href: whatsappLink() },
    { icon: "Mail", label: "Email", value: CONTACT.email, href: `mailto:${CONTACT.email}` },
    { icon: "Building2", label: "KVK", value: CONTACT.kvk },
  ];

  return (
    <div>
      <PageHero title={t.contact.title} crumb={t.nav.contact} bg={IMG.office2} sub={t.contact.sub} />

      <section className="mx-auto max-w-7xl px-5 md:px-8 py-20 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <div className="grid sm:grid-cols-2 gap-4">
            {cards.map((c) => {
              const inner = (
                <div className="h-full rounded-3xl bg-white p-6 border border-black/5 hover-lift">
                  <div className="grid place-items-center h-11 w-11 rounded-2xl bg-blue/10 text-blue"><Icon name={c.icon} size={20} /></div>
                  <div className="mt-4 text-sm text-slate-500">{c.label}</div>
                  <div className="font-semibold text-navy break-words">{c.value}</div>
                </div>
              );
              return c.href
                ? <a key={c.label} href={c.href} target="_blank" rel="noreferrer" data-testid={`contact-${c.label}`}>{inner}</a>
                : <div key={c.label}>{inner}</div>;
            })}
          </div>
          <div className="mt-4 rounded-3xl bg-navy p-7 text-white">
            <div className="flex items-center gap-3"><Icon name="Clock" size={20} className="text-blue" /><h3 className="font-display text-lg">{t.contact.hours}</h3></div>
            <p className="mt-3 text-white/70">Monday – Saturday: 07:00 – 20:00</p>
            <p className="text-white/70">Sunday: Closed</p>
            <p className="mt-2 text-blue font-medium">24/7 Emergency Service Available</p>
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="rounded-3xl bg-white p-8 border border-black/5 shadow-lg">
            <h2 className="font-display text-2xl text-navy">{t.contact.getInTouch}</h2>
            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <input required value={form.name} onChange={set("name")} placeholder={t.form.name} data-testid="contact-name"
                  className="rounded-xl border border-black/10 px-4 py-3 outline-none focus:border-blue" />
                <input required value={form.phone} onChange={set("phone")} placeholder={t.form.phone} data-testid="contact-phone"
                  className="rounded-xl border border-black/10 px-4 py-3 outline-none focus:border-blue" />
              </div>
              <input required type="email" value={form.email} onChange={set("email")} placeholder={t.form.email} data-testid="contact-email"
                className="w-full rounded-xl border border-black/10 px-4 py-3 outline-none focus:border-blue" />
              <textarea required rows={5} value={form.message} onChange={set("message")} placeholder={t.form.message} data-testid="contact-message"
                className="w-full rounded-xl border border-black/10 px-4 py-3 outline-none focus:border-blue resize-none" />
              <button type="submit" disabled={sending} data-testid="contact-submit"
                className="inline-flex items-center gap-2 rounded-full bg-blue px-7 py-3.5 font-semibold text-white hover:bg-[#0052FF] disabled:opacity-60">
                {sending ? <Icon name="Loader2" size={18} className="animate-spin" /> : <Icon name="Send" size={18} />}
                {sending ? t.form.sending : t.cta.send}
              </button>
            </form>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 md:px-8 pb-24">
        <div className="rounded-3xl overflow-hidden border border-black/5 shadow-lg">
          <iframe title="Sparko service area — Netherlands" data-testid="map"
            src="https://www.openstreetmap.org/export/embed.html?bbox=3.3%2C50.7%2C7.3%2C53.6&layer=mapnik"
            className="w-full h-[420px]" loading="lazy" />
        </div>
        <p className="mt-4 text-center text-slate-500">Proudly serving businesses and homes across the entire Netherlands.</p>
      </section>
    </div>
  );
}
